import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { AccountData, ActionSource, AuditLogService, PropelUserModel, UserService } from '../../../../api';
import { AuditLogData, isEventAuditLog, isOrderAuditLog } from '../../../../auditLog/types';
import { AuditLogChangeDetails } from '../../../../auditLog/components/AuditLogChangeDetails';
import { RefundAuditDetails } from '../../../../auditLog/components/RefundAuditDetails';
import { AuditLogEntityLink } from '../../../../auditLog/components/AuditLogEntityLink';
import { getAuditTypeIcon } from '../../../../auditLog/utils';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { getDateFormat, getTimeFormat } from '../../../../common/getDateAndTimeFormat';
import RotatingLoader from '../../../../common/ui/RotatingLoader';
import TitledDrawer from '../../../../common/ui/TitledDrawer';
import { AuditLogFlowVisualization } from '../../../../auditLog';

type EventActivityLogProps = {
  eventId: string;
  open: boolean;
  onClose: () => void;
};

export const EventActivityLog = ({ eventId, open, onClose }: EventActivityLogProps) => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { t } = useTranslation();
  const [auditLogs, setAuditLogs] = useState<AuditLogData[]>([]);
  const [users, setUsers] = useState<PropelUserModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const users = await UserService.getUsers();
        setUsers(users);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    void fetchUsers();
  }, []);

  useEffect(() => {
    const fetchAuditLogs = async () => {
      if (!open || !eventId) return;

      setIsLoading(true);
      try {
        const response = await AuditLogService.getAuditLogs(
          undefined, // auditTypes
          eventId, // eventId
          undefined, // passId
          undefined, // orderNumber
          undefined, // startDate
          undefined, // endDate
          1, // page
          1000, // perPage
        );
        setAuditLogs(response as AuditLogData[]);
      } catch (error) {
        console.error('Error fetching audit logs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchAuditLogs();
  }, [eventId, open]);

  const getAuditTypeLabel = (auditType: string): string => {
    return t(`auditlog.audit_types.${auditType}`);
  };

  const renderAuditLogItem = (log: AuditLogData) => {
    const user = users.find((user: PropelUserModel) => user.user_id === log.user_id);
    const resolvedName = user ? `${user.first_name} ${user.last_name}` : undefined;
    const userName =
      log.source === ActionSource.API
        ? `API${resolvedName ? ` (${resolvedName})` : ''}`
        : resolvedName ||
          (log.user_id === 'system'
            ? t('auditlog.system')
            : log.user_id
            ? t('auditlog.mbuser')
            : t('auditlog.customer'));

    return (
      <Box
        key={`${log.audit_type}-${log.created_at}`}
        sx={{
          py: 1.5,
          px: 0,
          borderBottom: '1px solid',
          borderColor: 'mb.lineSoft',
          '&:last-child': { borderBottom: 'none' },
        }}>
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <span style={{ fontSize: '14px' }}>{getAuditTypeIcon(log.audit_type)}</span>
          <Typography sx={{ fontWeight: 600 }}>{getAuditTypeLabel(log.audit_type)}</Typography>
          <Typography color="text.secondary">
            {moment(log.created_at).format(`${getDateFormat(account).longDateFormat} ${getTimeFormat(account)}`)}
          </Typography>
        </Box>

        {/* User and entity info */}
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <Typography>{userName}</Typography>
          <Typography variant="caption" color="text.secondary">
            •
          </Typography>
          <Box>
            <AuditLogEntityLink log={log} />
          </Box>

          {((isOrderAuditLog(log) && log.changes.length > 0) || (isEventAuditLog(log) && log.changes.length > 0)) && (
            <>
              <Typography variant="caption" color="text.secondary">
                •
              </Typography>
              {log.flow_chain_id && (
                <Box sx={{ marginInlineStart: '8px', mt: 0.5 }}>
                  <AuditLogFlowVisualization flowChainId={log.flow_chain_id} users={users} />
                </Box>
              )}
            </>
          )}
        </Box>

        {((isOrderAuditLog(log) && log.changes.length > 0) || (isEventAuditLog(log) && log.changes.length > 0)) && (
          <Box sx={{ marginInlineStart: '8px', mt: 0.5 }}>
            <AuditLogChangeDetails changes={log.changes} users={users} expanded hideChangesNumber />
          </Box>
        )}

        {log.refund && <RefundAuditDetails refund={log.refund} auditType={log.audit_type} />}
      </Box>
    );
  };

  return (
    <TitledDrawer title={t('auditlog.event_activity_log_title')} open={open} onClose={onClose}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
        {isLoading ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
            }}>
            <RotatingLoader />
          </Box>
        ) : auditLogs.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
            }}>
            <Typography variant="body2" color="text.secondary">
              {t('auditlog.no_activity_found')}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 2,
              py: 1,
            }}>
            {auditLogs.map(renderAuditLogItem)}
          </Box>
        )}
      </Box>
    </TitledDrawer>
  );
};
